"use client";
import Image from "next/image";
import EmailInvite from "@/components/app/events/manage/email-invite";
import { Text } from "@/components/ui/text";
import { Add, ArrowLeft2, InfoCircle, SearchNormal1, Send2, Trash } from "iconsax-react";
import { Fragment, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useModal } from "@/stores/use-modal-store";
import Box from "@/components/ui/box";
import Multiline from "@/components/ui/multiline";
import GoogleContacts from "@/components/app/events/manage/google-contacts";
import PastEventGuestList from "@/components/app/events/manage/past-invite-guest";
import Quill from "quill";
import _ from "lodash";
import eventClientApi from "@/api-client/event-client";
import dynamic from "next/dynamic";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { getRoleDisplayName } from "@/lib/utils";
import Group from "@/components/app/events/manage/group/group";
import { usePaticipantsInvite } from "@/stores/use-guest-invite-store";
import Cookies from "js-cookie";
import { clientServerIdKey, EVENT_INVITE_ROLES, getQueryKey } from "@key.ai/constants";
import { useQueryClient } from "@tanstack/react-query";
import { useEventStore } from "@/stores/use-event-store";
import SideDrawer from "@/components/ui/sidebar-drawer";
import CSVUpload from "@/components/app/events/manage/csv-upload";
import { pluralize } from "@/lib/label";
import EventIcon from "@/public/static/images/icons/Events-selected.svg";
import { X } from "lucide-react";
import NoDataView from "@/components/ui/no-data-view";
import { useUserQuery } from "@/hooks/use-user-query";
import { useInvitedUsers } from "@/hooks/use-invite-user-search";
import useInView from "@/hooks/use-in-view";
import useDebounce from "@/hooks/use-debounce";
import SkeletonListLoader from "@/components/app/manage-event/guest-skelaton";

const MailEditor = dynamic(() => import("@/components/editor/mail-editor"), {
  ssr: false
});

const GuestPreview = ({
  emails,
  handleRemoveEmail,
  eventData,
  resourceState,
  setResourceState,
  transformStateToPayload,
  getTotalCount,
  removeUserFromResourceState
}: any) => {
  const [search, setSearch] = useState<string>("");
  const debouncedSearchTerm = useDebounce<string>(search, 500);

  const {
    data: guestSearchList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
    status
  } = useInvitedUsers({
    eventId: eventData?.id,
    payload: { data: transformStateToPayload() },
    search: debouncedSearchTerm,
    enabled: getTotalCount(resourceState) > 0
  });

  const [guestSearchListRef, guestSearchListInView] = useInView({
    threshold: 0.01
  });

  useEffect(() => {
    if (guestSearchListInView && !isFetchingNextPage && hasNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [guestSearchListInView, isFetchingNextPage, hasNextPage, isLoading, fetchNextPage]);

  if (isLoading) {
    return <SkeletonListLoader />;
  }

  return (
    <div className="flex flex-col">
      <div className="px-l mt-[20px]">
        <Input
          variant="medium-s"
          className="rounded-xs w-full px-xl bg-[#F5F6F9]"
          inputClassName="w-full"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftSectionBgClassName="bg-transparent !mr-2 items-center content-center p-none m-auto"
          leftSection={
            <SearchNormal1 size="24" className="text-color-secondary" variant="Outline" />
          }
          rightSectionClassName="py-none px-none pr-m-nudge"
          rightSection={
            search && (
              <X
                size={20}
                onClick={() => {
                  setSearch("");
                }}
              ></X>
            )
          }
        />
      </div>
      <div className="mt-[20px] overflow-y-auto h-[calc(100dvh-250px)]">
        {guestSearchList && guestSearchList.pages.length > 0 ? (
          guestSearchList.pages.flatMap((page: any) =>
            page?.data?.map((email: any, index: number) => (
              <div key={`${email?.email}${index}}`}>
                <div className="px-l flex items-center justify-between p-s">
                  <Text variant={"body_one"}>{email?.email}</Text>
                  <X
                    onClick={() => {
                      removeUserFromResourceState(email);
                    }}
                    // onClick={() => handleRemoveEmail(email?.email)}
                    className="cursor-pointer"
                  />
                </div>
                <div className="border-b border-gray-200 w-full my-l"></div>
              </div>
            ))
          )
        ) : (
          <div className="content-center h-full mx-auto text-center">
            <NoDataView title="No guest found." imageKey="guest" />
          </div>
        )}
        {hasNextPage ? <div ref={guestSearchListRef} className="h-5" /> : null}
      </div>
    </div>
  );
};

const tabs = [
  {
    key: "email",
    title: "Email"
  },
  {
    key: "contact",
    title: "Contacts"
  },
  {
    key: "group",
    title: "Groups"
  },
  {
    key: "pastEvents",
    title: "Past events"
  },
  {
    key: "csv",
    title: "CSV"
  }
];

export type ViewKeys = "contact" | "email" | "pastEvents" | "group" | "csv";

export type ResourceType = "Group" | "Event" | "Email";

export type ResourceData = {
  resourceId: string | null;
  selectAll: boolean;
  selected: string[];
  deselected: string[];
  count: number;
};

export type ResourceState = {
  Group: Record<string, ResourceData>;
  Event: Record<string, ResourceData>;
  Email: Record<string, ResourceData>;
  Contact: Record<string, ResourceData>;
};

type ResourceItem = {
  resourceId?: string;
  selectAll: boolean;
  selected: string[];
  deselected?: string[];
  count: number;
};

type User = {
  email: string;
  resourceType: string;
  resourceId: string;
  user_id: string;
};

export const GuestInviteModal = ({ setOpenGuestInvite }: any) => {
  const initialState: ResourceState = {
    Group: {},
    Event: {},
    Email: {},
    Contact: {}
  };

  const [resourceState, setResourceState] = useState<ResourceState>(initialState);

  const [groups, setGroups] = useState<Record<string, ResourceData>>({});

  const [emails, setEmails] = useState<any>([]);

  const { showPaticipantsBar, setShowPaticipantsBar } = usePaticipantsInvite();

  const [quill, setQuill] = useState<Quill | null>(null);

  const [inviteLoading, setInviteLoading] = useState<boolean>(false);

  const [view, setView] = useState<ViewKeys>("email");

  const [emailView, setEmailView] = useState<boolean>(false);
  const [guestPreview, setGuestPreview] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const { eventData } = useEventStore();

  const { isOpen, onClose, type, data } = useModal();
  const { role }: any = data;

  const [subject, setSubject] = useState<string>(
    `Hi, ${"We"} invite you to join ${eventData?.name}.`
  );

  const community_id = Cookies.get(clientServerIdKey);

  const isModalOpen = isOpen && type === "inviteGuest";

  const handleTabChange = (value: ViewKeys) => {
    setView(value);
    setShowPaticipantsBar(true);
  };

  const userProfileInfo = useUserQuery({ userId: "me" });

  const { data: me } = userProfileInfo;

  useEffect(() => {
    if (eventData) {
      if (role === "guest") {
        setSubject(`You've been invited to attend ${eventData.name} by ${me?.firstName}`);
      } else {
        setSubject(
          `${me?.firstName} has invited you to be a ${getRoleDisplayName(role)} at ${eventData.name}!`
        );
      }
    }
    if (quill) {
      if (role === "guest") {
        quill.setText(`Great news! ${me?.firstName} has invited you to join ${eventData.name} 🥳`);
      } else {
        quill.setContents([
          { insert: `You've been invited to ${getRoleDisplayName(role)} at ` },
          { insert: eventData.name, attributes: { bold: true } },
          { insert: "! 🎉" }
        ]);
      }
    }
  }, [quill, me, eventData, role]);

  const handleToggleEmailSelection = (emailToToggle: any) => {
    setEmails(
      emails.map((email: any) =>
        email.email === emailToToggle.email ? { ...email, selected: !email.selected } : email
      )
    );
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter((email: any) => email?.email !== emailToRemove));
  };

  const countSelectedEmails = () => {
    return emails.filter((email: any) => email.selected).length;
  };

  type ResourceObject = {
    selectAll: boolean;
    selected: any[];
    deselected: any[];
    count?: number; // optional, since it's removed
  };

  type TransformedResource = {
    resourceID: string;
    selectAll: boolean;
    selected: any[];
    deselected: any[];
  };

  interface SingleResource {
    selectAll: boolean;
    selected: any[];
    deselected: any[];
    resourceId?: string | null;
  }

  const transformStateToPayload = (): {
    resourceType: string;
    resourceData: SingleResource[];
  }[] => {
    return Object.entries(resourceState).reduce(
      (acc, [resourceType, resources]) => {
        const resourceData = Object.entries(resources).map(([resourceId, { count, ...rest }]) => {
          // Base object without count
          const base: SingleResource = {
            ...rest
          };

          // If resourceType is NOT 'Email', add resourceId
          if (resourceType !== "Email") {
            base.resourceId = resourceId;
          }

          return base;
        });

        if (resourceData.length > 0) {
          if (resourceType === "Contact") {
            resourceData.some((item) => item.selected && item.selected.length > 0) &&
              acc.push({ resourceType, resourceData });
          } else {
            acc.push({ resourceType, resourceData });
          }
        }

        return acc;
      },
      [] as { resourceType: string; resourceData: SingleResource[] }[]
    );
  };

  const handleSendInvite = async () => {
    if (!quill) return;
    const description_html = quill.getSemanticHTML();
    const finaldescription = description_html.replace(
      /<img([^>]*)>/g,
      '<img$1 style="max-width:100%; height:auto;">'
    );
    setInviteLoading(true);
    try {
      const data = {
        // community_id: community_id,
        emailContent: {
          subject: subject.substring(0, 255),
          body: btoa(encodeURIComponent(finaldescription))
        },
        type: role,
        data: transformStateToPayload()
      };

      const response = await eventClientApi
        .post(`api/v1/events/${eventData?.id}/invite`, {
          json: { ...data }
        })
        .json<{ data: any[]; error_data: { message: string }[]; message: string }>();
      toast.success(response?.message);
      await queryClient.refetchQueries({
        queryKey: [getQueryKey("EVENT_GUEST", { eventId: eventData?.id })]
      });
      await queryClient.refetchQueries({
        queryKey: [getQueryKey("EVENT_PARTICIPATION_COUNT", { eventId: eventData?.id })]
      });
      handleClose();
    } catch (error: any) {
      console.log(error);
    } finally {
      setInviteLoading(false);
    }
  };

  const getTotalCount = (obj: any) => {
    let total = 0;

    if (obj["Email"]) {
      total += obj["Email"]["Email"]?.selected?.length || 0;
    }

    for (const key in obj) {
      if (key === "Email") continue;

      const subObj = obj[key];
      for (const id in subObj) {
        const resource = subObj[id];
        if (resource.selectAll === false) {
          // total += resource.count || 0;
          total += resource.selected?.length || 0;
        } else if (resource.selectAll === true) {
          const deselectedCount = resource.deselected?.length || 0;
          total += (resource.count || 0) - deselectedCount;
        }
      }
    }

    return total;
  };

  const handleSubmit = async () => {
    if (emailView && quill && getTotalCount(resourceState) > 0) {
      return handleSendInvite();
    }
    setGuestPreview(false);
    setEmailView(true);
  };

  const handleCancel = async () => {
    if (!emailView) {
      return handleClose();
    }
    setEmailView(false);
  };

  const handleClose = async () => {
    setEmails([]);
    setEmailView(false);
    onClose();
    setShowPaticipantsBar(true);
  };

  const viewComponents: Record<ViewKeys, React.ComponentType<any>> = {
    contact: GoogleContacts,
    email: EmailInvite,
    pastEvents: PastEventGuestList,
    group: Group,
    csv: CSVUpload
  };

  const updateResource = (
    type: keyof ResourceState,
    resourceId: string | null,
    data: Partial<ResourceData>
  ) => {
    setResourceState((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [resourceId ?? "null"]: {
          ...prev[type][resourceId ?? "null"],
          ...data
        }
      }
    }));
  };

  const checkIsUserPresent = (type: keyof ResourceState, resourceId: string, user: string) => {
    if (
      resourceState[type]?.[resourceId]?.selected?.includes(user) ||
      (resourceState[type]?.[resourceId]?.selectAll &&
        !resourceState[type]?.[resourceId]?.deselected?.includes(user))
    ) {
      return true;
    }
    return false;
  };

  const checkIfAllSelect = (type: keyof ResourceState, resourceId: string) => {
    return (
      resourceState[type]?.[resourceId]?.selectAll &&
      resourceState[type]?.[resourceId]?.deselected?.length == 0
    );
  };

  const toggleUserSelection = (
    type: keyof ResourceState,
    resourceId: string,
    user: string,
    select: boolean
  ) => {
    const resource = resourceState[type][resourceId] || {};
    if (Object.keys(resource).length === 0) {
      const updatedSelected = [user];
      const updatedDeselected: any = [];

      let newData = {
        [type]: {
          ...resourceState[type],
          [resourceId]: {
            resourceId: resourceId,
            selectAll: false,
            selected: updatedSelected,
            deselected: updatedDeselected,
            count: updatedSelected.length
          }
        }
      };
      setResourceState((prev) => ({
        ...prev,
        ...newData
      }));
    } else {
      let updatedSelected;
      let updatedDeselected;
      let updatesCount;
      // console.log("select", select);
      if (resource.selectAll && !select) {
        if (resource.deselected && resource.deselected.includes(user)) {
          // console.log("User is already deselected",select);
          updatedDeselected = resource?.deselected?.filter((u) => u !== user);
          updatedSelected = resource?.selected;
          // updatesCount = resource?.count + 1;
        } else {
          // console.log("else user is deselected",select);
          updatedDeselected =
            resource.deselected && resource.deselected.length > 0
              ? [...resource.deselected, user]
              : [user];
          updatedSelected =
            resource?.selected?.length > 0 ? [...resource.selected?.filter((u) => u !== user)] : [];
          // updatesCount = resource?.count - 1;
        }
      } else {
        if (resource.selected.includes(user) && !select) {
          // console.log("User is already selected",select);
          updatedSelected =
            resource?.selected?.length > 0
              ? [...resource.selected?.filter((u) => u !== user)]
              : [user];
          // updatesCount = resource.count - 1;
        } else {
          // console.log("else user is selected",select);
          updatedSelected = resource?.selected?.length > 0 ? [...resource.selected, user] : [user];
          // updatesCount = resource.count + 1;
        }
      }
      updatesCount = resource?.count;
      const newData = {
        ...resourceState,
        [type]: {
          ...resourceState[type],
          [resourceId]: {
            resourceId: resourceId,
            selectAll: resource?.selectAll,
            selected: updatedSelected,
            deselected: updatedDeselected,
            count: updatesCount
          }
        }
      };
      setResourceState({ ...newData });
    }
  };

  const toggleSelectAll = (
    type: keyof ResourceState,
    resourceId: string,
    users: string[],
    selectAll: boolean,
    total: number,
    googleContacts?: boolean
  ) => {
    setResourceState((prev) => {
      const currentType = prev[type] || {};
      const currentResource = currentType[resourceId] || {};
      return {
        ...prev,
        [type]: {
          ...currentType,
          [resourceId]: {
            ...currentResource,
            selectAll: selectAll,
            selected: googleContacts ? users : [],
            deselected: [],
            count: selectAll ? total : users.length
          }
        }
      };
    });
  };

  const handleBulkAddEmils = (
    type: keyof ResourceState,
    resourceId: string,
    users: string[],
    selectAll: boolean,
    total: number
  ) => {
    setResourceState((prev) => {
      const currentType = prev[type] || {};
      const currentResource = currentType[resourceId] || {};

      return {
        ...prev,
        [type]: {
          ...currentType,
          [resourceId]: {
            ...currentResource,
            resourceId,
            selectAll: selectAll,
            selected: [...(currentResource?.selected ? currentResource?.selected : []), ...users],
            deselected: [],
            count: total || users.length
          }
        }
      };
    });
  };

  const removeUserFromResourceState = (user: User) => {
    const { email, resourceType, resourceId } = user;
    const typeKey = capitalizeFirstLetter(resourceType) as keyof ResourceState;

    if (!resourceState[typeKey] || !resourceState[typeKey][resourceId]) return;

    const resource = resourceState[typeKey][resourceId];

    const updatedSelected = [...(resource.selected || [])];
    const updatedDeselected = [...(resource.deselected || [])];
    let updatedCount = resource.count || 0;

    if (resource.selectAll) {
      if (!updatedDeselected.includes(email)) {
        updatedDeselected.push(email);
        updatedCount -= 1;
      }
    } else {
      const index = updatedSelected.indexOf(email);
      if (index !== -1) {
        updatedSelected.splice(index, 1);
        updatedCount -= 1;
      }
    }

    const updatedResource = {
      ...resource,
      selected: updatedSelected,
      deselected: updatedDeselected,
      count: updatedCount
    };

    setResourceState((prev) => ({
      ...prev,
      [typeKey]: {
        ...prev[typeKey],
        [resourceId]: updatedResource
      }
    }));
    toast.success(`Removed ${email} from ${resourceType} resource.`);
  };

  const capitalizeFirstLetter = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const bunchToggleUserSelection = (
    type: keyof ResourceState,
    resourceId: string,
    users: string[]
  ) => {
    users.forEach((user) => {
      const isSelected = checkIsUserPresent(type, resourceId, user);
      toggleUserSelection(type, resourceId, user, !isSelected);
    });
  };

  const renderViewComponent = (view: ViewKeys, props: any): JSX.Element | null => {
    const Component = viewComponents[view];
    return Component ? <Component {...props} /> : null;
  };

  return (
    <SideDrawer
      isOpen={isModalOpen}
      onClose={() => onClose()}
      className="md:w-[50%] w-full lg:w-[40%] bg-container bg-white rounded-none px-0 overflow-hidden"
    >
      <div className="py-m px-l flex justify-between items-start">
        <div className="flex gap-m items-start">
          <div
            className={`transition-all duration-300 ease-in-out mt-[3px] ${
              guestPreview ? "opacity-100 block" : "opacity-0 hidden"
            }`}
            onClick={() => setGuestPreview(false)}
          >
            <ArrowLeft2 className="transform transition-transform duration-300 ease-in-out cursor-pointer" />
          </div>
          <div
            className={`transition-all duration-300 ease-in-out ${
              !guestPreview ? "ml-0" : "ml-[1px]"
            }`}
          >
            <Text
              variant={"title_three"}
              className="font-semibold transition-all duration-300 ease-in-out"
            >
              {/* Invite {EVENT_INVITE_ROLES[role]} (
              {
                emails?.filter(
                  (item: any, index: any, self: any) =>
                    index === self?.findIndex((t: any) => t.email === item.email)
                ).length
              }
              ) */}
              Invite {EVENT_INVITE_ROLES[role]?.toLowerCase()}s ({getTotalCount(resourceState)})
            </Text>

            {!guestPreview ? (
              <Text
                variant={"body_one"}
                className="text-color-tertiary underline cursor-pointer"
                onClick={() => {
                  setGuestPreview(true);
                  setEmailView(false);
                }}
              >
                View list
              </Text>
            ) : (
              <Text
                variant={"body_one"}
                className="text-danger-500  cursor-pointer"
                onClick={() => {
                  setEmails([]);
                  setResourceState(initialState);
                  setGuestPreview(false);
                }}
              >
                Remove all
              </Text>
            )}
          </div>
        </div>

        <div>
          <X className="cursor-pointer" onClick={onClose} />
        </div>
      </div>
      <div className="flex flex-row mb-3 mt-m">
        {!emailView && !guestPreview
          ? tabs.map((section, index) => (
              <Button
                key={index}
                variant="text"
                radius="none"
                layerClassName="hover:bg-transparent"
                onClick={() => handleTabChange(section.key as ViewKeys)}
                className={`h-fit w-auto md:px-[30px]
                  lg:px-[40px] ${view === section.key ? "border-b-1 border-black" : ""}`}
              >
                {section.title}
              </Button>
            ))
          : null}
      </div>
      <div className="border-b border-gray-200 w-full mt-[-12px]"></div>
      <div className=" w-full  h-auto">
        <div>
          {guestPreview && (
            <div style={{ display: guestPreview ? "block" : "none" }}>
              <GuestPreview
                handleRemoveEmail={handleRemoveEmail}
                emails={emails}
                eventData={eventData}
                resourceState={resourceState}
                setResourceState={setResourceState}
                transformStateToPayload={transformStateToPayload}
                getTotalCount={getTotalCount}
                removeUserFromResourceState={removeUserFromResourceState}
              />
            </div>
          )}
          <div style={{ display: emailView ? "block" : "none" }} className="px-l">
            <Text variant={"body_one"} className="mt-xl">
              Subject
            </Text>

            <Input
              variant="medium-s"
              className="rounded-xs w-full px-l"
              inputClassName="w-full"
              placeholder="Subject"
              value={subject}
              onChange={(e: any) => setSubject(e.target.value)}
            />

            <Text variant={"body_one"} className="mt-xl ">
              About Event
            </Text>

            <Box className="field-border">
              <div className="">
                <MailEditor quill={quill} setQuill={setQuill} />
              </div>
            </Box>
            <Multiline
              className="bg-transparent !px-0 !mx-0 mt-xl"
              icon={<InfoCircle size="32" className="rotate-180" />}
              titleText={"We will send (Guests/Featured Guests/Hosts) an invite link"}
              text1={
                "Hosts & Featured Guests will automatically be added to the guest list upon accepting the invite. Guests will still have to register for the event."
              }
              titleTextSize={"medium"}
              text1Size={"small"}
              text1Classname={true}
            />
          </div>
          <div style={{ display: !guestPreview && !emailView ? "block" : "none" }}>
            {renderViewComponent(view, {
              emails,
              setEmails,
              role,
              setGuestPreview,
              resourceState,
              setResourceState,
              toggleUserSelection,
              bunchToggleUserSelection,
              checkIsUserPresent,
              toggleSelectAll,
              checkIfAllSelect,
              removeUserFromResourceState,
              handleBulkAddEmils
            })}
          </div>
        </div>

        <Box className="w-full bg-white flex gap-m justify-end mt-s rounded-0  fixed bottom-0 mx-auto ">
          {emailView ? (
            <Button className="w-[150px]" variant="secondary" onClick={handleCancel}>
              <Text variant={"body_two"}>Back</Text>
            </Button>
          ) : null}
          <Button
            className="w-[150px]"
            onClick={handleSubmit}
            disabled={inviteLoading || getTotalCount(resourceState) === 0 || !subject}
            loading={inviteLoading}
            loaderProps={{ size: "xs" }}
          >
            <Text variant={"body_two"} className=" items-center flex gap-s">
              <Send2 size={20} />
              Send Invite
            </Text>
          </Button>
        </Box>
      </div>
    </SideDrawer>
  );
};
